/**
 * Reine Logik des gemeinsamen KI-Assistenzfensters (`KiAssistent.tsx`).
 *
 * ═══ WARUM DIESE DATEI ═════════════════════════════════════════════════════
 * Das Fenster steht über drei Werkzeugen (Plan, Gewerke-Überschneidung,
 * Handbuch) und muss für jedes dieselbe Frage beantworten: WAS von einer
 * KI-Antwort darf überhaupt übernommen werden, und was ist schon da? Diese
 * Entscheidung ist der eigentliche Wert des Fensters — und sie gehört nicht in
 * eine Komponente: das Projekt übersetzt seine Tests in der Node-Umgebung
 * (kein jsdom), eine Regel ist also nur dann geprüft, wenn sie HIER steht.
 * Stehende Entscheidung im Repo: dünner Container, Logik in puren Funktionen.
 *
 * ═══ DIE DREI AUFGABEN ═════════════════════════════════════════════════════
 *   1. ABBILDUNG. Ein Vorschlag wird zu einem übernehmbaren Datensatz — für
 *      die Koordinierung ein `NeuesKoordinierungPaar` mit `quelle: 'ki'` und
 *      OHNE Bestätigung, für das Handbuch eine zusammengeführte
 *      Baustellenordnung. Angelegt wird hier nichts; das tun die Module.
 *   2. DOPPELERKENNUNG. Was der Regelkatalog oder eine gespeicherte Bewertung
 *      schon sagt, wird nicht ein zweites Mal angelegt.
 *   3. ZUSAMMENFASSUNG. Der Unterschied zwischen „nichts gefunden" und „nicht
 *      erreichbar" muss auf dem Bildschirm stehen und nicht im Fehlertext
 *      versteckt sein.
 *
 * Rein: kein DOM, kein React, kein Supabase, kein `fetch`.
 */

import { paarKey } from '../koordinierung/koordinierungLogic';
import type { NeuesKoordinierungPaar } from '../koordinierung/koordinierungLogic';
import type {
    AiAssistErgebnis,
    EinrichtungVorschlag,
    HandbuchAssistAntwort,
    HandbuchAssistKontext,
    KoordinierungAssistAntwort,
    KoordinierungAssistKontext,
    KoordinierungKiVorschlag,
    OrdnungVorschlag,
    PlanAssistAntwort,
    PlanAssistKontext,
} from '../../services/aiAssistContext';
import type { Baustelleneinrichtung, Baustellenordnung } from '../handbuch/types';

// ═══════════════════════════════════════════════════════════════════════════
// 1) WER FRAGT — und was er dem Fenster anbietet
// ═══════════════════════════════════════════════════════════════════════════

/** Die drei Reiter der Hülle. Zeichengleich zu `SigePlanReiter`. */
export type KiReiter = 'plan' | 'koordinierung' | 'handbuch';

/**
 * Ein Gewerkepaar, wie der Assistent es für den Abgleich braucht.
 *
 * Bewusst eine eigene, schmale Form und NICHT `AbgleichZeile`: die Zeile trägt
 * Ampel, Kennzahlen und ganze Katalogvorschläge mit sich, und die
 * Doppelerkennung soll ohne den halben Koordinierungszweig prüfbar bleiben.
 * Der Aufrufer schreibt Feld für Feld ab, was er hat.
 */
export interface KiPaarZeile {
    aNorm: string;
    aLabel: string;
    bNorm: string;
    bLabel: string;
    /** Das gerechnete Zeitfenster, `null` bei einer überholten Bewertung. */
    berechnet: { von: string; bis: string; dauerTage: number } | null;
    /** Gefährdungstexte, die zu diesem Paar bereits GESPEICHERT sind. */
    gespeicherteGefaehrdungen: string[];
    /** Gefährdungstexte, die der REGELKATALOG zu diesem Paar vorschlägt. */
    katalogGefaehrdungen: string[];
}

/**
 * Was ein Reiter beim Öffnen des Fensters herausgibt.
 *
 * Ein Paket je Reiter, damit die Fallunterscheidung EINMAL an der Grenze
 * stattfindet und nicht in jeder Anzeigefunktion noch einmal.
 */
export type KiKontextPaket =
    | { art: 'plan'; kontext: PlanAssistKontext }
    | {
        art: 'koordinierung';
        kontext: KoordinierungAssistKontext;
        zeilen: KiPaarZeile[];
        /** Vermerk auf die bewertete Planfassung; wandert an die neue Zeile. */
        sigeProjektId: string | null;
    }
    | {
        art: 'handbuch';
        kontext: HandbuchAssistKontext;
        bestand: Baustellenordnung | null;
        /** Der Stand der Baustelleneinrichtung — inklusive offenem Entwurf,
         *  aus demselben Grund wie bei `bestand`: sonst schlägt der Assistent
         *  vor, was gerade ungespeichert im Formular steht. */
        bestandEinrichtung: Baustelleneinrichtung | null;
    };

/** Was beim Öffnen des Fensters feststeht. */
export interface KiOeffnung {
    paket: KiKontextPaket;
    /**
     * Darf dieser Benutzer JETZT übernehmen? Fragen darf er immer.
     *
     * Wird beim Öffnen gelesen und nicht laufend nachgeführt — die zweite
     * Reihe des Schreibschutzes steht in `uebernimm` und im Dienst, so wie im
     * ganzen Haus. Eine veraltete Freigabe kann deshalb nichts schreiben.
     */
    darfUebernehmen: boolean;
    /** Warum nicht — für den Balken im Fenster. `null`, wenn erlaubt. */
    sperrgrund: string | null;
}

/**
 * Was übernommen werden soll. Der Plan-Reiter übernimmt NICHTS (siehe unten).
 *
 * Baustellenordnung und Baustelleneinrichtung sind zwei EIGENE Arten und
 * nicht ein Fall mit zwei optionalen Feldern. Sie hängen an verschiedenen
 * Papieren — die Ordnung am Aushang für den Bauzaun, die Einrichtung an
 * Kapitel 3 des Handbuchs —, und sie werden einzeln angehakt. Ein gemeinsamer
 * Fall mit `ordnung?`/`einrichtung?` ließe sich mit beiden Feldern leer
 * aufrufen, und ein Vorschlag ginge still ins Nichts.
 */
export type KiUebernahme =
    | { art: 'koordinierung'; entwuerfe: NeuesKoordinierungPaar[] }
    | { art: 'handbuch'; ordnung: Baustellenordnung; herkunft: OrdnungHerkunft }
    | {
        art: 'handbuch-einrichtung';
        einrichtung: Baustelleneinrichtung;
        herkunft: EinrichtungHerkunft;
    };

/**
 * Der Draht zwischen einem Reiter und dem Fenster der Hülle.
 *
 * ═══ WARUM DIE HÜLLE NICHT SELBST SCHREIBT ═════════════════════════════════
 * Die Daten liegen im Reiter — die Koordinierung hält ihre Bewertungen und
 * Katalogvorschläge bereits im Speicher, das Handbuch seinen Entwurf. Schriebe
 * die Hülle daran vorbei in die Datenbank, sähe das offene Modul die neue
 * Zeile nicht, und der Anwender hielte sie für nicht angelegt. Deshalb baut
 * der Reiter den Kontext und führt die Übernahme aus; das Fenster zeigt nur.
 */
export interface KiBruecke {
    /** Läuft EINMAL beim Öffnen des Fensters. Nicht auf Vorrat. */
    oeffne: () => Promise<KiOeffnung>;
    /** Führt die Übernahme aus und gibt die Meldung für das Fenster zurück. */
    uebernimm: (auswahl: KiUebernahme) => Promise<string>;
}

// ═══════════════════════════════════════════════════════════════════════════
// 2) NICHT ERREICHBAR IST NICHT DASSELBE WIE NICHTS GEFUNDEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die drei Assistenten-Fälle brauchen je einen `case` im gemini-proxy UND
 * einen Deploy. Bis dahin antwortet der Proxy mit „Unbekannte Action: …", und
 * dieser Text kommt unverändert durch `describeAiError` hindurch (er ist
 * ausdrücklich kein vorübergehender Fehler, siehe `aiRetry.test.ts`).
 *
 * Ohne diese Unterscheidung läse sich ein fehlender Deploy wie „die KI hat
 * nichts gefunden" — der Anwender hielte seinen Plan für vollständig, obwohl
 * niemand hingesehen hat. Das ist die gefährlichere Verwechslung, deshalb
 * bekommt sie einen eigenen Zustand und einen eigenen Satz.
 */
export const istNichtFreigeschaltet = (fehler?: string | null): boolean =>
    /unbekannte action|unknown action/i.test(fehler || '');

export const NICHT_FREIGESCHALTET_TITEL = 'Die KI-Funktion ist noch nicht freigeschaltet';

export const NICHT_FREIGESCHALTET_TEXT =
    'Diese Auswertung ist auf dem Server noch nicht ausgerollt. Es wurde gefragt, aber niemand '
    + 'hat geantwortet — das ist etwas anderes als „nichts gefunden". Alles andere in diesem '
    + 'Modul arbeitet unverändert weiter.';

/** In welchem Zustand das Fenster gerade ist. */
export type AntwortLage =
    | 'unbenutzt'
    | 'laeuft'
    | 'nicht_freigeschaltet'
    | 'fehler'
    | 'leer'
    | 'treffer';

export interface Zusammenfassung {
    lage: AntwortLage;
    ueberschrift: string;
    text: string;
    /** Wieviele Einträge die Antwort trägt. */
    anzahl: number;
    /** Die Antwort war am Token-Limit abgeschnitten und wurde gerettet. */
    unvollstaendig: boolean;
}

/** Die nutzbaren Daten eines Umschlags — bei `ok: false` gibt es KEINE. */
export const nutzbareAntwort = <T>(ergebnis?: AiAssistErgebnis<T> | null): T | null =>
    (ergebnis && ergebnis.ok ? ergebnis.daten : null);

export const anzahlPlan = (antwort?: PlanAssistAntwort | null): number =>
    (antwort
        ? antwort.fehlendeArbeitsschritte.length + antwort.fehlendeGefaehrdungen.length
            + antwort.luecken.length + (antwort.antwort ? 1 : 0)
        : 0);

export const anzahlKoordinierung = (antwort?: KoordinierungAssistAntwort | null): number =>
    (antwort ? antwort.vorschlaege.length + antwort.hinweise.length : 0);

export const anzahlHandbuch = (antwort?: HandbuchAssistAntwort | null): number => {
    if (!antwort) return 0;
    const o = antwort.ordnung;
    const e = antwort.einrichtung;
    const felder = [o.arbeitszeiten, o.zugang, o.sammelplatz].filter(Boolean).length;
    const einrichtungsFelder = [
        e.versorgung, e.entsorgung, e.lagerplatz, e.verkehrsfuehrung, e.bemerkung,
    ].filter(Boolean).length;
    return felder + einrichtungsFelder + o.regeln.length + o.notfallschritte.length
        + antwort.fehlendeAngaben.length + antwort.hinweise.length;
};

/**
 * Ein Satz über den Stand der Antwort.
 *
 * `anzahl` kommt vom Aufrufer und wird hier nicht erraten: was ein „Treffer"
 * ist, weiß nur der Reiter — im Plan zählt eine Fließtext-Antwort mit, in der
 * Koordinierung nicht.
 */
export const fasseZusammen = (
    ergebnis: AiAssistErgebnis<unknown> | null | undefined,
    anzahl: number,
    laeuft: boolean,
): Zusammenfassung => {
    if (laeuft) {
        return {
            lage: 'laeuft',
            ueberschrift: 'Die KI sieht sich den Stand an',
            text: 'Das dauert einige Sekunden. Übernommen wird nichts von allein.',
            anzahl: 0,
            unvollstaendig: false,
        };
    }
    if (!ergebnis) {
        return {
            lage: 'unbenutzt',
            ueberschrift: 'Noch nicht gefragt',
            text: 'Der Assistent sieht sich erst auf Knopfdruck etwas an. Was er vorschlägt, '
                + 'wird angezeigt und erst nach Ihrer Auswahl übernommen.',
            anzahl: 0,
            unvollstaendig: false,
        };
    }
    if (!ergebnis.ok) {
        if (istNichtFreigeschaltet(ergebnis.fehler)) {
            return {
                lage: 'nicht_freigeschaltet',
                ueberschrift: NICHT_FREIGESCHALTET_TITEL,
                text: NICHT_FREIGESCHALTET_TEXT,
                anzahl: 0,
                unvollstaendig: false,
            };
        }
        return {
            lage: 'fehler',
            ueberschrift: 'Die KI war nicht erreichbar',
            text: (ergebnis.fehler || 'Unbekannter Fehler')
                + ' — das heißt NICHT, dass es nichts zu ergänzen gäbe. Bitte später erneut versuchen.',
            anzahl: 0,
            unvollstaendig: false,
        };
    }
    if (anzahl <= 0) {
        return {
            lage: 'leer',
            ueberschrift: 'Nichts zu ergänzen gefunden',
            text: 'Die KI war erreichbar und hat zu diesem Stand nichts vorgeschlagen. '
                + 'Sie ersetzt keine Prüfung durch den Koordinator.',
            anzahl: 0,
            unvollstaendig: ergebnis.unvollstaendig,
        };
    }
    return {
        lage: 'treffer',
        ueberschrift: `${anzahl} Vorschlag/Vorschläge zur Prüfung`,
        text: ergebnis.unvollstaendig
            ? 'Die Antwort war am Längenlimit abgeschnitten und wurde bis zum letzten '
                + 'vollständigen Eintrag gerettet — es kann mehr geben, als hier steht.'
            : 'Nichts davon ist übernommen. Sie entscheiden Punkt für Punkt.',
        anzahl,
        unvollstaendig: ergebnis.unvollstaendig,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) DOPPELERKENNUNG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vergleichsform für Freitexte: klein, ohne Umlaute, ohne Satzzeichen.
 *
 * Sie dient AUSSCHLIESSLICH dem Vergleich; gespeichert wird immer der
 * Wortlaut. Umlaute werden aufgelöst, weil derselbe Sachverhalt im Katalog
 * „Absturz durch ungesicherte Öffnung" und in einer KI-Antwort
 * „Absturz durch ungesicherte Oeffnung" heißen kann.
 */
export const normalisiereVergleichstext = (wert?: string | null): string =>
    (wert || '')
        .toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

/**
 * Ab dieser Länge gilt ein enthaltener Text als derselbe Sachverhalt.
 *
 * Die Schwelle ist bewusst hoch. Ein übersehener Doppeleintrag ist eine Zeile
 * zuviel, die jeder sieht und löschen kann; ein fälschlich als Dublette
 * verworfener Vorschlag ist eine Gefährdung, die stillschweigend verschwindet.
 * Deshalb bleibt die Regel eng: Gleichheit, oder Enthaltensein eines
 * hinreichend langen Textes.
 */
export const MIN_TEILTEXT = 20;

/**
 * Sagt der NEUE Text nichts, was nicht schon dastünde?
 *
 * ═══ DIE RICHTUNG IST DIE GANZE REGEL ══════════════════════════════════════
 * Enthaltensein wirkte hier ursprünglich in BEIDE Richtungen — und verwarf
 * damit genau das Gegenteil einer Dublette. Gespeichert stand „Gefahr durch
 * herabfallende Teile"; die KI schlug „Gefahr durch herabfallende Teile beim
 * gleichzeitigen Kranbetrieb über dem Gerüst" vor. Der Vorschlag benennt die
 * Ursache, das Zusammentreffen und den Ort — und wurde weggeworfen, weil der
 * kürzere Bestandstext in ihm steckt. Im Handbuch dasselbe: „Helmpflicht auf
 * der gesamten Baustelle" schluckte „…, auch im Bürocontainer".
 *
 * Deshalb zählt nur EINE Richtung: Ein neuer Text, der im vorhandenen enthalten
 * ist, sagt nichts Neues — das ist eine Dublette. Ein neuer Text, der den
 * vorhandenen ENTHÄLT, sagt mehr; er bleibt wählbar.
 *
 * ═══ WARUM NICHT NUR EIN HINWEIS ═══════════════════════════════════════════
 * Beides zusammen: die Erweiterung bleibt anlegbar (`findeErweiterung` liefert
 * den Bezug für einen Hinweis am Haken), damit der Gegenleser die kürzere
 * Bestandszeile bewusst stehenlassen oder streichen kann. Ein reiner Hinweis
 * OHNE Richtungsregel würde dagegen auch echte Wiederholungen wieder
 * durchlassen — die Sperre bleibt, sie zeigt nur nicht mehr in die falsche
 * Richtung.
 */
export const istTextDublette = (neu: string, vorhandene: (string | null | undefined)[]): boolean => {
    const a = normalisiereVergleichstext(neu);
    if (!a) return false;
    return (vorhandene || []).some(v => {
        const b = normalisiereVergleichstext(v);
        if (!b) return false;
        if (a === b) return true;
        return a.length >= MIN_TEILTEXT && b.includes(a);
    });
};

/**
 * Welchen vorhandenen Text erweitert dieser Vorschlag wörtlich?
 *
 * Gegenstück zu `istTextDublette`: keine Sperre, sondern der Bezug für einen
 * Satz am Haken. Ohne ihn stünden nach der Übernahme zwei Zeilen fast gleichen
 * Wortlauts nebeneinander, und niemand sähe, dass die eine die andere ablöst.
 * Zurück kommt der WORTLAUT des Bestands (nicht die Vergleichsform) — der
 * Anwender soll die Zeile wiedererkennen, die er vor sich hat.
 */
export const findeErweiterung = (
    neu: string,
    vorhandene: (string | null | undefined)[],
): string | null => {
    const a = normalisiereVergleichstext(neu);
    if (!a) return null;
    for (const v of vorhandene || []) {
        const b = normalisiereVergleichstext(v);
        if (!b || b === a) continue;
        if (b.length >= MIN_TEILTEXT && a.includes(b)) return (v || '').trim();
    }
    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) KOORDINIERUNG: VORSCHLAG → ÜBERNEHMBARER DATENSATZ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `neu`       — anlegbar.
 * `dublette`  — dieselbe Gefährdung steht zu diesem Paar schon da.
 * `kein_paar` — die genannten Gewerke ergeben im Plan kein Paar. Nicht
 *               anlegbar: die Zeile stünde sonst bei den falschen beiden
 *               Gewerken (dieselbe Überlegung wie in `alsKiVorschlag`).
 */
export type UebernahmeBefund = 'neu' | 'dublette' | 'kein_paar';

export interface KiUebernahmeVorschlag {
    /** Stabil über das Zeichnen hinweg — Schlüssel für Auswahlkästchen. */
    id: string;
    vorschlag: KoordinierungKiVorschlag;
    befund: UebernahmeBefund;
    /** Ein Satz, warum. Steht auch bei `neu`, damit die Liste sich liest. */
    hinweis: string;
    /** Nur bei `neu` gefüllt: der Datensatz, der angelegt würde. */
    entwurf: NeuesKoordinierungPaar | null;
}

/**
 * Baut aus einem KI-Vorschlag die Zeile, die angelegt würde.
 *
 * ═══ DREI FESTLEGUNGEN, DIE NICHT VERHANDELBAR SIND ════════════════════════
 *   • `quelle: 'ki'` — im Nachhinein muss erkennbar bleiben, was aus dem
 *     Regelwerk kam und was eine Maschine vorgeschlagen hat.
 *   • `bestaetigt_am: null` — ein Vorschlag ist ein Vorschlag, bis ein Mensch
 *     ihn bestätigt. Wer hier stempelte, machte daraus eine Feststellung des
 *     Koordinators (wortgleiche Überlegung wie bei `paarAusVorschlag`).
 *   • `katalog_version: null` — eine KI-Antwort ist nicht reproduzierbar und
 *     trägt keine Fassung. Eine Fassung daranzuschreiben behauptete einen
 *     Nachweis, den dieser Eintrag nicht führen kann.
 *
 * Begründung, Raumbezug und Fundstellen des Modells wandern NICHT mit: die
 * Zeile hat keine Spalte dafür, und eine Paragraphenangabe, die ein Modell
 * geraten hat, darf nicht als Fundstelle im Koordinierungsbericht landen. Im
 * Fenster stehen sie zur Beurteilung, aufs Papier kommen sie nicht.
 */
export const paarAusKiVorschlag = (
    zeile: KiPaarZeile,
    vorschlag: KoordinierungKiVorschlag,
    sigeProjektId: string | null = null,
): NeuesKoordinierungPaar => ({
    sige_projekt_id: sigeProjektId || null,
    gewerk_a_norm: zeile.aNorm,
    gewerk_b_norm: zeile.bNorm,
    gewerk_a_label: zeile.aLabel || '',
    gewerk_b_label: zeile.bLabel || '',
    ueberschneidung_von: zeile.berechnet ? zeile.berechnet.von : null,
    ueberschneidung_bis: zeile.berechnet ? zeile.berechnet.bis : null,
    dauer_tage: zeile.berechnet ? zeile.berechnet.dauerTage : null,
    gefaehrdung: vorschlag.gefaehrdung,
    massnahme: vorschlag.massnahme || '',
    top: null,
    prioritaet: vorschlag.prioritaet,
    quelle: 'ki',
    katalog_version: null,
    bestaetigt_am: null,
    bestaetigt_von_name: null,
    uebernommen_matrix_am: null,
    veraltet_seit: null,
});

/**
 * Ordnet jeden Vorschlag einem Gewerkepaar zu und entscheidet, ob er neu ist.
 *
 * Die Gewerksnamen kommen als ANZEIGETEXT zurück (so gingen sie in den
 * Kontext). Zugeordnet wird über die Vergleichsform, und das Paar selbst über
 * `paarKey` — dadurch findet auch ein Vorschlag mit vertauschter Reihenfolge
 * („Elektro × Rohbau" statt „Rohbau × Elektro") dieselbe Zeile. Ohne das
 * entstünde je Paarung eine zweite, spiegelbildliche Bewertung, und der
 * Unique-Index der Datenbank griffe daran vorbei.
 */
export const planeKoordinierungUebernahme = (
    antwort: KoordinierungAssistAntwort | null | undefined,
    zeilen: KiPaarZeile[] | null | undefined,
    optionen: { sigeProjektId?: string | null } = {},
): KiUebernahmeVorschlag[] => {
    const vorschlaege = (antwort && antwort.vorschlaege) || [];
    if (!vorschlaege.length) return [];

    const nachName = new Map<string, string>();
    const nachSchluessel = new Map<string, KiPaarZeile>();
    for (const z of zeilen || []) {
        if (!z) continue;
        // Anzeigename UND maschineller Schlüssel zählen als Schreibweise: das
        // Modell bekommt zwar die Anzeigenamen, gibt aber gelegentlich den
        // Katalogbegriff zurück ("elektro" statt "Elektro NU 2").
        for (const [name, norm] of [
            [z.aLabel, z.aNorm], [z.aNorm, z.aNorm], [z.bLabel, z.bNorm], [z.bNorm, z.bNorm],
        ] as const) {
            const schluessel = normalisiereVergleichstext(name);
            if (schluessel) nachName.set(schluessel, norm);
        }
        nachSchluessel.set(paarKey(z.aNorm, z.bNorm), z);
    }

    // Was in DIESEM Durchgang schon geplant ist. Ein Modell wiederholt sich;
    // zwei gleichlautende Zeilen anzulegen wäre eine Dublette, die niemand
    // vorgeschlagen hat.
    const schonGeplant = new Map<string, string[]>();

    return vorschlaege.map((v, i) => {
        const aNorm = nachName.get(normalisiereVergleichstext(v.a));
        const bNorm = nachName.get(normalisiereVergleichstext(v.b));
        const grundlos = (hinweis: string): KiUebernahmeVorschlag => ({
            id: `${i}-ohne-paar`, vorschlag: v, befund: 'kein_paar', hinweis, entwurf: null,
        });

        if (!aNorm || !bNorm) {
            return grundlos(
                `„${v.a}" und „${v.b}" lassen sich den Gewerken dieses Plans nicht zuordnen. `
                + 'Nicht übernehmbar — die Bewertung stünde sonst beim falschen Paar.',
            );
        }
        if (aNorm === bNorm) {
            return grundlos('Beide Nennungen zeigen auf dasselbe Gewerk. Ein Paar ist das nicht.');
        }

        const schluessel = paarKey(aNorm, bNorm);
        const zeile = nachSchluessel.get(schluessel);
        if (!zeile) {
            return grundlos(
                `Für „${v.a} × ${v.b}" rechnet der Plan keine gemeinsame Bauzeit. `
                + 'Prüfen Sie die Zeiträume der Arbeitsschritte, bevor Sie das bewerten.',
            );
        }

        const geplant = schonGeplant.get(schluessel) || [];
        const dublette = (grund: string): KiUebernahmeVorschlag => ({
            id: `${i}-${schluessel}`, vorschlag: v, befund: 'dublette', hinweis: grund, entwurf: null,
        });
        if (istTextDublette(v.gefaehrdung, zeile.gespeicherteGefaehrdungen)) {
            return dublette('Zu diesem Paar ist dieselbe Gefährdung bereits erfasst.');
        }
        if (istTextDublette(v.gefaehrdung, zeile.katalogGefaehrdungen)) {
            return dublette('Der Regelkatalog deckt das für dieses Paar bereits ab — '
                + 'übernehmen Sie den Katalogvorschlag, er trägt eine Fassung.');
        }
        if (istTextDublette(v.gefaehrdung, geplant)) {
            return dublette('Dieser Vorschlag steht in derselben Antwort schon einmal.');
        }

        // Kein Grund zum Verwerfen, aber einer zum Hinsehen: der Vorschlag
        // schreibt eine vorhandene Zeile fort. Er wird angelegt — welche der
        // beiden Fassungen bleibt, entscheidet der Gegenleser in der Liste.
        const erweitert = findeErweiterung(v.gefaehrdung, [
            ...zeile.gespeicherteGefaehrdungen, ...zeile.katalogGefaehrdungen, ...geplant,
        ]);

        schonGeplant.set(schluessel, [...geplant, v.gefaehrdung]);
        return {
            id: `${i}-${schluessel}`,
            vorschlag: v,
            befund: 'neu',
            hinweis: erweitert
                ? `Erweitert die vorhandene Angabe „${erweitert}" — beide Zeilen stünden danach `
                    + 'nebeneinander. Bitte prüfen, ob die kürzere entbehrlich wird.'
                : 'Wird als unbestätigte Bewertung mit Herkunft „KI" angelegt. '
                    + 'Erst Ihre Bestätigung macht daraus eine Festlegung.',
            entwurf: paarAusKiVorschlag(zeile, v, optionen.sigeProjektId ?? null),
        };
    });
};

/** Die Entwürfe der ausgewählten Vorschläge — alles andere fällt heraus. */
export const entwuerfeAus = (
    geplant: KiUebernahmeVorschlag[],
    auswahl?: ReadonlySet<string> | null,
): NeuesKoordinierungPaar[] =>
    (geplant || [])
        .filter(g => g.befund === 'neu' && !!g.entwurf && (!auswahl || auswahl.has(g.id)))
        .map(g => g.entwurf as NeuesKoordinierungPaar);

// ═══════════════════════════════════════════════════════════════════════════
// 5) HANDBUCH: ORDNUNGS-VORSCHLAG IN DEN ENTWURF MISCHEN
// ═══════════════════════════════════════════════════════════════════════════

/** Die drei Freitextfelder der Baustellenordnung. */
export type OrdnungFeld = 'arbeitszeiten' | 'zugang' | 'sammelplatz';

export const ORDNUNG_FELD_LABEL: Record<OrdnungFeld, string> = {
    arbeitszeiten: 'Arbeitszeiten',
    zugang: 'Zugang zur Baustelle',
    sammelplatz: 'Sammelplatz',
};

export const LEERE_ORDNUNG: Baustellenordnung = {
    arbeitszeiten: '',
    zugang: '',
    regeln: [],
    notfallschritte: [],
    sammelplatz: '',
};

/**
 * Was ein Vorschlag beisteuern KANN — und zugleich der Herkunftsnachweis für
 * das, was tatsächlich übernommen wurde.
 *
 * Dieselbe Form für beides, weil es dieselbe Frage ist: welche Punkte kommen
 * aus der KI? Der Entwurfsbalken des Handbuchs zeigt sie an, bevor gespeichert
 * wird — die Baustellenordnung hat keine Herkunftsspalte, und ein Vermerk im
 * TEXT stünde später auf dem Aushang am Bauzaun.
 */
export interface OrdnungHerkunft {
    felder: OrdnungFeld[];
    regeln: string[];
    notfallschritte: string[];
    anzahl: number;
}

export const LEERE_HERKUNFT: OrdnungHerkunft = {
    felder: [], regeln: [], notfallschritte: [], anzahl: 0,
};

const alsOrdnung = (bestand?: Baustellenordnung | null): Baustellenordnung => ({
    arbeitszeiten: bestand?.arbeitszeiten || '',
    zugang: bestand?.zugang || '',
    regeln: [...(bestand?.regeln || [])],
    notfallschritte: [...(bestand?.notfallschritte || [])],
    sammelplatz: bestand?.sammelplatz || '',
});

const gefuellt = (wert?: string | null): boolean => !!(wert || '').trim();

/**
 * Was der Vorschlag zum vorhandenen Stand beitragen würde.
 *
 * ═══ ZWEI REGELN, DIE NICHTS VERLIEREN DÜRFEN ══════════════════════════════
 *   • Die drei Freitexte werden NUR angeboten, wenn sie leer sind. Was ein
 *     Mensch dort aufgeschrieben hat, überschreibt keine Maschine — und
 *     „Zugang" und „Sammelplatz" beschreiben oft eine örtliche Vereinbarung,
 *     die das Modell gar nicht kennen kann (es bekommt sie bewusst nicht).
 *   • Regeln und Notfallschritte werden ERGÄNZT. Vorhandene Zeilen bleiben in
 *     ihrer Reihenfolge stehen; angeboten wird nur, was noch nicht dasteht.
 */
export const offeneOrdnungsPunkte = (
    bestand: Baustellenordnung | null | undefined,
    vorschlag: OrdnungVorschlag | null | undefined,
): OrdnungHerkunft => {
    if (!vorschlag) return LEERE_HERKUNFT;
    const alt = alsOrdnung(bestand);

    const felder: OrdnungFeld[] = (['arbeitszeiten', 'zugang', 'sammelplatz'] as OrdnungFeld[])
        .filter(f => gefuellt(vorschlag[f]) && !gefuellt(alt[f]));

    const neueZeilen = (neu: string[], vorhanden: string[]): string[] => {
        const gesammelt: string[] = [];
        for (const zeile of neu || []) {
            if (!gefuellt(zeile)) continue;
            if (istTextDublette(zeile, [...vorhanden, ...gesammelt])) continue;
            gesammelt.push(zeile.trim());
        }
        return gesammelt;
    };

    const regeln = neueZeilen(vorschlag.regeln || [], alt.regeln);
    const notfallschritte = neueZeilen(vorschlag.notfallschritte || [], alt.notfallschritte);

    return {
        felder,
        regeln,
        notfallschritte,
        anzahl: felder.length + regeln.length + notfallschritte.length,
    };
};

/** Was der Anwender angehakt hat. Fehlt sie, gilt alles Offene als gewählt. */
export interface OrdnungAuswahl {
    felder?: string[];
    regeln?: string[];
    notfallschritte?: string[];
}

export interface OrdnungUebernahme {
    /** Der vollständige Entwurf, wie er in das Modul gesetzt wird. */
    entwurf: Baustellenordnung;
    /** Was davon aus der KI stammt — sichtbar bis zum Speichern. */
    herkunft: OrdnungHerkunft;
}

/**
 * Mischt den Vorschlag in den vorhandenen Stand.
 *
 * Ergebnis ist ein ENTWURF und kein Schreibvorgang: das Handbuchmodul setzt
 * ihn in seinen vorhandenen Entwurfsweg, der Anwender sieht ihn im blauen
 * Balken und speichert selbst. Genommen wird ausschließlich, was
 * `offeneOrdnungsPunkte` anbietet — auch dann, wenn die Auswahl mehr enthält.
 * So kann eine veraltete Auswahl (der Bestand hat sich inzwischen gefüllt)
 * nichts überschreiben.
 */
export const mischeOrdnung = (
    bestand: Baustellenordnung | null | undefined,
    vorschlag: OrdnungVorschlag | null | undefined,
    auswahl?: OrdnungAuswahl | null,
): OrdnungUebernahme => {
    const entwurf = alsOrdnung(bestand);
    const offen = offeneOrdnungsPunkte(bestand, vorschlag);
    if (!vorschlag || offen.anzahl === 0) return { entwurf, herkunft: LEERE_HERKUNFT };

    const gewaehlt = (offene: string[], gewuenscht?: string[]): string[] =>
        (gewuenscht ? offene.filter(w => gewuenscht.includes(w)) : offene);

    const felder = gewaehlt(offen.felder, auswahl?.felder) as OrdnungFeld[];
    const regeln = gewaehlt(offen.regeln, auswahl?.regeln);
    const notfallschritte = gewaehlt(offen.notfallschritte, auswahl?.notfallschritte);

    for (const feld of felder) entwurf[feld] = (vorschlag[feld] || '').trim();
    // Angehängt, nicht ersetzt: der Bestand behält seine Reihenfolge, und die
    // Ergänzungen stehen am Ende — dort sucht sie der Gegenleser.
    entwurf.regeln = [...entwurf.regeln, ...regeln];
    entwurf.notfallschritte = [...entwurf.notfallschritte, ...notfallschritte];

    return {
        entwurf,
        herkunft: {
            felder,
            regeln,
            notfallschritte,
            anzahl: felder.length + regeln.length + notfallschritte.length,
        },
    };
};

/** Zwei Herkunftsnachweise zusammenführen — zweimal übernehmen verliert den
 *  ersten Nachweis sonst, und der Balken behauptete danach zuwenig. */
export const vereineHerkunft = (a: OrdnungHerkunft, b: OrdnungHerkunft): OrdnungHerkunft => {
    const eindeutig = (x: string[], y: string[]) => Array.from(new Set([...x, ...y]));
    const felder = eindeutig(a.felder, b.felder) as OrdnungFeld[];
    const regeln = eindeutig(a.regeln, b.regeln);
    const notfallschritte = eindeutig(a.notfallschritte, b.notfallschritte);
    return {
        felder,
        regeln,
        notfallschritte,
        anzahl: felder.length + regeln.length + notfallschritte.length,
    };
};

/** Ein Satz für den Entwurfsbalken: was steht darin aus der KI? */
export const herkunftSatz = (herkunft: OrdnungHerkunft | null | undefined): string => {
    if (!herkunft || herkunft.anzahl === 0) return '';
    const teile: string[] = [];
    if (herkunft.felder.length) {
        teile.push(herkunft.felder.map(f => ORDNUNG_FELD_LABEL[f]).join(', '));
    }
    if (herkunft.regeln.length) teile.push(`${herkunft.regeln.length} Regel(n)`);
    if (herkunft.notfallschritte.length) {
        teile.push(`${herkunft.notfallschritte.length} Notfallschritt(e)`);
    }
    return `Aus dem KI-Vorschlag: ${teile.join(' · ')}. Bitte vor dem Speichern gegenlesen.`;
};

// ═══════════════════════════════════════════════════════════════════════════
// 5b) HANDBUCH: EINRICHTUNGS-VORSCHLAG IN DEN ENTWURF MISCHEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Zweiter Teil desselben Reiters, aber ein EIGENER Weg. Die Baustellenordnung
// hängt am Aushang für den Bauzaun, die Baustelleneinrichtung an Kapitel 3 des
// Handbuchs — wer das eine ergänzen lässt, darf sich nicht nebenbei das andere
// verändern. Deshalb zwei Herkunftsnachweise, zwei Auswahlen, zwei Balkentexte.

/** Die fünf Freitextfelder der Baustelleneinrichtung. */
export type EinrichtungFeld = keyof Baustelleneinrichtung;

export const EINRICHTUNG_FELD_LABEL: Record<EinrichtungFeld, string> = {
    versorgung: 'Versorgung',
    entsorgung: 'Entsorgung',
    lagerplatz: 'Lagerflächen',
    verkehrsfuehrung: 'Verkehrsführung',
    bemerkung: 'Bemerkung',
};

export const LEERE_EINRICHTUNG: Baustelleneinrichtung = {
    versorgung: '',
    entsorgung: '',
    lagerplatz: '',
    verkehrsfuehrung: '',
    bemerkung: '',
};

/**
 * Wie ein Vorschlag in ein Feld kommt.
 *
 * Der Unterschied MUSS auf dem Bildschirm stehen: „setzen" füllt ein leeres
 * Feld, „ergänzen" hängt einen Absatz unter einen Text, den ein Mensch
 * geschrieben hat. Das Zweite ist der Eingriff, den ein Gegenleser sehen will,
 * bevor er speichert.
 */
export type EinrichtungArt = 'setzen' | 'ergaenzen';

export interface EinrichtungPunkt {
    feld: EinrichtungFeld;
    art: EinrichtungArt;
    /** Der Text, der gesetzt oder angehängt würde. */
    text: string;
}

export interface EinrichtungHerkunft {
    punkte: EinrichtungPunkt[];
    anzahl: number;
}

export const LEERE_EINRICHTUNG_HERKUNFT: EinrichtungHerkunft = { punkte: [], anzahl: 0 };

const alsEinrichtung = (bestand?: Baustelleneinrichtung | null): Baustelleneinrichtung => ({
    versorgung: bestand?.versorgung || '',
    entsorgung: bestand?.entsorgung || '',
    lagerplatz: bestand?.lagerplatz || '',
    verkehrsfuehrung: bestand?.verkehrsfuehrung || '',
    bemerkung: bestand?.bemerkung || '',
});

const EINRICHTUNG_FELDER: EinrichtungFeld[] = [
    'versorgung', 'entsorgung', 'lagerplatz', 'verkehrsfuehrung', 'bemerkung',
];

/**
 * Was der Vorschlag zum vorhandenen Stand beitragen würde.
 *
 * Anders als bei den drei Ordnungs-Freitexten wird ein GEFÜLLTES Feld hier
 * nicht übergangen, sondern zur Ergänzung angeboten. Der Grund liegt im
 * Kontext: `baueHandbuchKontext` gibt den Wortlaut der Einrichtungsfelder mit,
 * das Modell weiß also, was schon dasteht, und schreibt der Aufgabe nach das
 * FEHLENDE dazu (Zugang und Sammelplatz der Ordnung bekommt es bewusst nicht
 * — dort wäre jeder Vorschlag geraten, und deshalb gilt dort die strengere
 * Regel).
 *
 * Überschrieben wird trotzdem nie: `ergaenzen` hängt an, `setzen` gibt es nur
 * für ein leeres Feld. Und was inhaltlich schon dasteht, filtert
 * `istTextDublette` — derselbe Vergleich wie bei den Ordnungsregeln.
 */
export const offeneEinrichtungsPunkte = (
    bestand: Baustelleneinrichtung | null | undefined,
    vorschlag: EinrichtungVorschlag | null | undefined,
): EinrichtungHerkunft => {
    if (!vorschlag) return LEERE_EINRICHTUNG_HERKUNFT;
    const alt = alsEinrichtung(bestand);

    const punkte: EinrichtungPunkt[] = [];
    for (const feld of EINRICHTUNG_FELDER) {
        const text = (vorschlag[feld] || '').trim();
        if (!text) continue;
        const vorhanden = alt[feld].trim();
        if (!vorhanden) {
            punkte.push({ feld, art: 'setzen', text });
            continue;
        }
        // Zeilenweise verglichen: das Modell antwortet auf ein gefülltes Feld
        // erfahrungsgemäß mit dem Bestand PLUS einer neuen Zeile. Ohne die
        // Zerlegung stünde der ganze Absatz ein zweites Mal im Feld.
        const neueZeilen = text.split('\n')
            .map(z => z.trim())
            .filter(z => !!z && !istTextDublette(z, [vorhanden, ...punkte.map(p => p.text)]));
        if (!neueZeilen.length) continue;
        punkte.push({ feld, art: 'ergaenzen', text: neueZeilen.join('\n') });
    }

    return { punkte, anzahl: punkte.length };
};

export interface EinrichtungUebernahme {
    entwurf: Baustelleneinrichtung;
    herkunft: EinrichtungHerkunft;
}

/**
 * Mischt den Einrichtungs-Vorschlag in den vorhandenen Stand.
 *
 * `auswahl` sind die Felder, die der Anwender angehakt hat; fehlt sie, gilt
 * alles Offene als gewählt. Genommen wird ausschließlich, was
 * `offeneEinrichtungsPunkte` anbietet — eine veraltete Auswahl (das Feld hat
 * sich inzwischen gefüllt) kann damit nichts überschreiben.
 */
export const mischeEinrichtung = (
    bestand: Baustelleneinrichtung | null | undefined,
    vorschlag: EinrichtungVorschlag | null | undefined,
    auswahl?: string[] | null,
): EinrichtungUebernahme => {
    const entwurf = alsEinrichtung(bestand);
    const offen = offeneEinrichtungsPunkte(bestand, vorschlag);
    if (offen.anzahl === 0) return { entwurf, herkunft: LEERE_EINRICHTUNG_HERKUNFT };

    const genommen = auswahl
        ? offen.punkte.filter(p => auswahl.includes(p.feld))
        : offen.punkte;

    for (const punkt of genommen) {
        entwurf[punkt.feld] = punkt.art === 'setzen'
            ? punkt.text
            : `${entwurf[punkt.feld].replace(/\s+$/, '')}\n${punkt.text}`;
    }

    return { entwurf, herkunft: { punkte: genommen, anzahl: genommen.length } };
};

/** Zwei Einrichtungs-Herkunftsnachweise zusammenführen — aus demselben Grund
 *  wie `vereineHerkunft`: zweimal übernehmen darf den ersten Nachweis nicht
 *  verlieren. Verglichen wird über Feld UND Text, weil dasselbe Feld erst
 *  gesetzt und später ergänzt worden sein kann. */
export const vereineEinrichtungHerkunft = (
    a: EinrichtungHerkunft,
    b: EinrichtungHerkunft,
): EinrichtungHerkunft => {
    const gesehen = new Set<string>();
    const punkte: EinrichtungPunkt[] = [];
    for (const p of [...a.punkte, ...b.punkte]) {
        const schluessel = `${p.feld}|${p.text}`;
        if (gesehen.has(schluessel)) continue;
        gesehen.add(schluessel);
        punkte.push(p);
    }
    return { punkte, anzahl: punkte.length };
};

/** Ein Satz für den Entwurfsbalken. */
export const einrichtungHerkunftSatz = (
    herkunft: EinrichtungHerkunft | null | undefined,
): string => {
    if (!herkunft || herkunft.anzahl === 0) return '';
    const teile = herkunft.punkte.map(p => (p.art === 'ergaenzen'
        ? `${EINRICHTUNG_FELD_LABEL[p.feld]} (ergänzt)`
        : EINRICHTUNG_FELD_LABEL[p.feld]));
    return `Aus dem KI-Vorschlag zur Baustelleneinrichtung: ${teile.join(' · ')}. `
        + 'Bitte vor dem Speichern gegenlesen.';
};

/** Was das Fenster sagt, wenn zur Ordnung nichts Offenes übrigbleibt. */
export interface OrdnungsLeerMeldung {
    /** `warnung`, wenn die Leere auch am Abschneiden liegen kann. */
    ton: 'entwarnung' | 'warnung';
    text: string;
}

/**
 * „Nichts zu ergänzen" darf nur stehen, wenn auch wirklich alles da war.
 *
 * Reißt die Antwort am Token-Limit ab, wird der Ordnungsteil verworfen (er ist
 * dann halb geschrieben und als Vorschlag unbrauchbar) — übrig bleiben die
 * geretteten Hinweise. Das sah bisher aus wie eine Entwarnung: „was
 * vorgeschlagen wurde, steht dort bereits". Der Anwender hielte seine
 * Baustellenordnung damit für geprüft, obwohl niemand sie zu Ende gelesen hat.
 * Dieselbe Verwechslung wie beim fehlenden Deploy weiter oben, nur eine Ebene
 * tiefer.
 *
 * Defensiv gegen eine FEHLENDE Angabe: nur ein ausdrückliches `false` entwarnt.
 * Kommt der Umschlag ohne die Angabe (alter Server, geänderte Antwortform),
 * gilt der ungünstigere Fall — Schweigen ist hier kein Freispruch.
 */
export const ordnungsLeerMeldung = (unvollstaendig?: boolean | null): OrdnungsLeerMeldung =>
    (unvollstaendig === false
        ? {
            ton: 'entwarnung',
            text: 'Zur Baustellenordnung gibt es nichts zu ergänzen: was vorgeschlagen wurde, '
                + 'steht dort bereits — oder das Feld ist von Hand gefüllt und wird nicht '
                + 'überschrieben.',
        }
        : {
            ton: 'warnung',
            text: 'Die Antwort war am Längenlimit abgeschnitten; der Vorschlag zur '
                + 'Baustellenordnung kam nur halb an und wurde deshalb verworfen. Das heißt '
                + 'NICHT, dass dort nichts zu ergänzen wäre — bitte mit einer engeren Frage '
                + 'noch einmal fragen.',
        });

// ═══════════════════════════════════════════════════════════════════════════
// 6) REITERWECHSEL: WAS NUR IM SPEICHER STEHT, DARF NICHT STILL VERFALLEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Grund, unter dem das Handbuch seinen offenen Entwurf anmeldet.
 *
 * Zeichengleich zum `holdIdleExit('handbuch')` im Handbuchmodul. Diesen Weg
 * benutzt die Hülle bewusst mit, statt sich eine zweite Meldekette zu bauen:
 * das Modul kennt seinen Entwurfszustand bereits und meldet ihn dort an, wo er
 * ohnehin gebraucht wird (Leerlauf-Rückzug). Eine eigene Prop wäre eine zweite
 * Wahrheit über denselben Sachverhalt — und die eine, die man beim nächsten
 * Entwurfsfeld vergisst.
 */
export const HANDBUCH_ENTWURF_GRUND = 'handbuch';

/**
 * Muss vor diesem Reiterwechsel zurückgefragt werden?
 *
 * Die Hülle mountet nur den offenen Reiter; der Ordnungs-Entwurf und sein
 * Herkunftsnachweis sind Zustand des Handbuch-Moduls und fallen mit ihm weg.
 * Nach „Übernommen — die Punkte stehen jetzt im ENTWURF" liegt der Reiter
 * „Plan" einen Zentimeter neben der Erfolgsmeldung; ein Klick dorthin warf
 * bisher alles weg, ohne zu fragen.
 *
 * Zurückgefragt wird nur beim VERLASSEN des Handbuchs — ein Wechsel innerhalb
 * desselben Reiters baut nichts ab, und die beiden anderen Reiter halten keinen
 * ungespeicherten Stand.
 *
 * @returns Der Rückfragetext, oder `null`, wenn nichts verlorengeht.
 */
export const entwurfsRueckfrage = (
    von: KiReiter,
    nach: KiReiter,
    offeneGruende: string[] | null | undefined,
): string | null => {
    if (von !== 'handbuch' || nach === 'handbuch') return null;
    if (!(offeneGruende || []).includes(HANDBUCH_ENTWURF_GRUND)) return null;
    return 'Im Handbuch stehen ungespeicherte Angaben — der Entwurf der Baustellenordnung '
        + 'oder ein angefangener Änderungsvermerk. Beim Reiterwechsel gehen sie verloren; '
        + 'gespeichert wird nur über den blauen Balken im Handbuch.';
};
